import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import WallPlanner from "../components/WallPlanner";
import HorizontalPlanner from "../components/HorizontalPlanner";
import DashboardView from "../components/DashboardView";
import ProjectDialog from "../components/ProjectDialog";
import ProjectListView from "../components/ProjectListView";
import FinanceView from "../components/FinanceView";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, PanelLeftOpen, LayoutGrid, Calendar as CalendarIcon, Euro, List as ListIcon } from "lucide-react";
import { MONTHS_DE, STATUS_VALUES } from "../lib/slots";
import { extendEndByVisibleNotes, buildExemptRefs } from "../lib/projectDates";

function formatErr(detail) {
  if (!detail) return "Aktion fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const STATUS_FILTER_KEY = "wallplanner_status_filter_v2";
const SIDEBAR_COLLAPSED_KEY = "wallplanner_sidebar_collapsed";
const PLANNER_SORT_KEY = "wallplanner_planner_sort";
const PLANNER_ORIENTATION_KEY = "wallplanner_orientation";
const PLANNER_ZOOM_KEY = "wallplanner_horizontal_zoom";
const VALID_STATUSES = new Set(STATUS_VALUES);

// Start-Einstellung: alle Status ausgewählt. Gespeichertes leeres Array (früher
// „alle") wird als „alle Status" übernommen; eine leere Auswahl entsteht nur manuell.
const ALL_STATUSES = [...STATUS_VALUES];
function loadInitialFilter() {
  if (typeof window === "undefined") return ALL_STATUSES;
  const stored = window.localStorage.getItem(STATUS_FILTER_KEY);
  if (!stored || stored === "all") return ALL_STATUSES;
  if (!stored.startsWith("[")) {
    return VALID_STATUSES.has(stored) ? [stored] : ALL_STATUSES;
  }
  try {
    const arr = JSON.parse(stored);
    if (!Array.isArray(arr) || arr.length === 0) return ALL_STATUSES;
    return arr.filter((v) => VALID_STATUSES.has(v));
  } catch {
    return ALL_STATUSES;
  }
}

function loadInitialCollapsed() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
}

export default function PlannerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Return state handed back from ProjectDetailPage (Liste → Projekt → Speichern → Liste).
  const restore = location.state?.restore || null;
  const [projects, setProjects] = useState([]);
  const [specialDays, setSpecialDays] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => Array.isArray(restore?.statusFilter) ? restore.statusFilter : loadInitialFilter());
  const today = new Date();
  const [anchor, setAnchor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(loadInitialCollapsed);
  const [sidebarExpanded, setSidebarExpanded] = useState(restore?.sidebarExpanded ?? true);
  const listStateRef = useRef(restore?.list || null);
  const [view, setView] = useState(restore?.view || "dashboard"); // "dashboard" | "planner" | "list" | "finance"
  const [plannerSort, setPlannerSort] = useState(() => {
    if (typeof window === "undefined") return "milestone";
    const v = window.localStorage.getItem(PLANNER_SORT_KEY);
    // Backwards compat: legacy stored values were "default" or "milestone".
    if (v === "default" || v === "milestone" || v === "milestone_main") return v;
    return "milestone";
  });
  const [orientation, setOrientation] = useState(() => {
    if (typeof window === "undefined") return "vertical";
    const v = window.localStorage.getItem(PLANNER_ORIENTATION_KEY);
    return v === "horizontal" ? "horizontal" : "vertical";
  });
  const [horizontalZoom, setHorizontalZoom] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = Number(window.localStorage.getItem(PLANNER_ZOOM_KEY));
    // Supported: 1 (100%), 1.5 (150%), 2 (200%). Anything else falls back to 100%.
    if (v === 2) return 2;
    if (v === 1.5) return 1.5;
    return 1;
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  // Pending milestone-drop awaiting confirmation. Shape:
  // { project, newDateIso, notesBefore } | null
  const [pendingMilestoneMove, setPendingMilestoneMove] = useState(null);
  // Pending internal-planning-note move that needs a confirmation because the
  // new date crosses the project's main milestone.
  const [pendingNoteMove, setPendingNoteMove] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    try { window.localStorage.setItem(STATUS_FILTER_KEY, JSON.stringify(statusFilter)); } catch { /* */ }
  }, [statusFilter]);

  // Rückkehrzustand nur einmal verwenden: nach dem Lesen aus der History entfernen,
  // damit ein späterer Reload regulär im Dashboard startet.
  useEffect(() => {
    if (location.state?.restore) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0"); } catch { /* */ }
  }, [sidebarCollapsed]);

  useEffect(() => {
    try { window.localStorage.setItem(PLANNER_SORT_KEY, plannerSort); } catch { /* */ }
  }, [plannerSort]);

  useEffect(() => {
    try { window.localStorage.setItem(PLANNER_ORIENTATION_KEY, orientation); } catch { /* */ }
  }, [orientation]);

  useEffect(() => {
    try { window.localStorage.setItem(PLANNER_ZOOM_KEY, String(horizontalZoom)); } catch { /* */ }
  }, [horizontalZoom]);

  const reload = useCallback(async () => {
    try {
      const [{ data: projData }, { data: sdData }] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/special-days`),
      ]);
      setProjects(projData);
      setSpecialDays(Array.isArray(sdData) ? sdData : []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openProject = (p, listState) => {
    if (listState) listStateRef.current = listState;
    navigate(`/projekt/${p.id}`, { state: { from: { view, statusFilter, sidebarExpanded, list: listStateRef.current } } });
  };

  const handleCellClick = (dateKey, slot, project) => {
    if (project) {
      // Existing project → dedicated wide detail page.
      openProject(project);
      return;
    }
    setEditing({
      name: "",
      client: "",
      start_date: dateKey,
      end_date: dateKey,
      slot,
      status: "active",
    });
    setDialogOpen(true);
  };

  const handleCreateFromSidebar = () => {
    const today = new Date();
    const isoToday = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
    setEditing({
      name: "", client: "", start_date: isoToday, end_date: isoToday, slot: 1, status: "active",
    });
    setDialogOpen(true);
  };

  const handleSelectProject = (p, listState) => {
    if (!p?.id) return;
    openProject(p, listState);
  };

  const handleSave = async (form) => {
    // Manual milestone-date edits in the dialog inherit the exact drag-drop
    // rules: validate against start_date, warn if planning notes exist before
    // the new date, and extend end_date if the new milestone falls after it
    // (never shorten). If a confirmation is required we defer the save.
    const originalProj = form.id ? projects.find((p) => p.id === form.id) : null;
    // Sichtbare Planungsnotizen (Planer-Häkchen) verlängern das Projektende, nie verkürzen.
    form.end_date = extendEndByVisibleNotes(form.end_date, form.planning_notes, buildExemptRefs(form));
    const milestoneChanged =
      originalProj &&
      form.milestone_date &&
      form.milestone_date !== (originalProj.milestone_date || "");
    if (milestoneChanged) {
      if (form.milestone_date < form.start_date) {
        toast.error("Meilenstein liegt vor dem Projekt-Startdatum.");
        return;
      }
      // Extend end_date if milestone falls after it (mirrors commitMilestoneMove).
      if (form.milestone_date > form.end_date) form.end_date = form.milestone_date;
      const notesBefore = (Array.isArray(originalProj.planning_notes) ? originalProj.planning_notes : [])
        .filter((n) => n?.date && n.date >= originalProj.start_date && n.date < form.milestone_date);
      if (notesBefore.length > 0) {
        setPendingMilestoneMove({
          project: originalProj,
          newDateIso: form.milestone_date,
          notesBefore,
          savePayload: form,
        });
        return;
      }
    }
    try {
      if (form.id) {
        const { id, created_at, ...payload } = form;
        await axios.put(`${API}/projects/${id}`, payload);
        toast.success("Projekt aktualisiert");
      } else {
        await axios.post(`${API}/projects`, form);
        toast.success("Projekt erstellt");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  // Persist only the main milestone_date (and end_date if extended).
  // Planning notes and slot remain untouched.
  const commitMilestoneMove = async (proj, newDateIso) => {
    const newEnd = newDateIso > proj.end_date ? newDateIso : proj.end_date;
    const { id, created_at, ...rest } = proj;
    const payload = { ...rest, milestone_date: newDateIso, end_date: newEnd };
    try {
      await axios.put(`${API}/projects/${id}`, payload);
      toast.success("Meilenstein verschoben");
      reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const handleMilestoneDrop = async (projectId, newDateIso) => {
    const proj = projects.find((p) => p.id === projectId);
    if (!proj || !newDateIso || newDateIso === proj.milestone_date) return;

    if (newDateIso < proj.start_date) {
      toast.error("Meilenstein liegt vor dem Projekt-Startdatum.");
      return;
    }

    const notesBefore = (Array.isArray(proj.planning_notes) ? proj.planning_notes : [])
      .filter((n) => n?.date && n.date >= proj.start_date && n.date < newDateIso);

    if (notesBefore.length > 0) {
      // Defer the actual save behind an in-app confirmation modal — window.confirm
      // can be suppressed inside drop event handlers in some browsers.
      setPendingMilestoneMove({ project: proj, newDateIso, notesBefore });
      return;
    }

    await commitMilestoneMove(proj, newDateIso);
  };

  const commitPlanningNoteMove = async (proj, noteIdx, newDateIso) => {
    const notes = Array.isArray(proj.planning_notes) ? [...proj.planning_notes] : [];
    if (noteIdx < 0 || noteIdx >= notes.length) return;
    notes[noteIdx] = { ...notes[noteIdx], date: newDateIso };
    const { id, created_at, ...rest } = proj;
    try {
      await axios.put(`${API}/projects/${id}`, { ...rest, planning_notes: notes, end_date: extendEndByVisibleNotes(rest.end_date, notes, buildExemptRefs(rest)) });
      toast.success("Planungs-Symbol verschoben");
      reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const handlePlanningNoteDrop = async (projectId, noteIdx, newDateIso) => {
    const proj = projects.find((p) => p.id === projectId);
    if (!proj || !newDateIso) return;
    const notes = Array.isArray(proj.planning_notes) ? proj.planning_notes : [];
    const note = notes[noteIdx];
    if (!note || note.date === newDateIso) return;
    // Only the note's date changes — project period, milestone, slot, status stay.
    // If the move crosses the main milestone (or was equal to it), warn the user.
    const ms = proj.milestone_date;
    if (ms && note.date) {
      const crossed =
        (note.date <= ms && newDateIso > ms) ||
        (note.date >= ms && newDateIso < ms);
      if (crossed) {
        setPendingNoteMove({ project: proj, noteIdx, newDateIso, milestoneDate: ms, noteTitle: note.title });
        return;
      }
    }
    await commitPlanningNoteMove(proj, noteIdx, newDateIso);
  };

  // Switch the main view. Der Statusfilter bleibt beim Ansichtswechsel erhalten.
  const switchView = (next) => {
    if (next === view) return;
    setView(next);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Projekt wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success("Projekt gelöscht");
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const visibleProjects = useMemo(
    () =>
      projects
        .filter((p) => statusFilter.includes(p.status))
        .sort((a, b) => {
          if (a.slot !== b.slot) return a.slot - b.slot;
          if (a.start_date !== b.start_date) return a.start_date < b.start_date ? -1 : 1;
          return a.name.localeCompare(b.name);
        }),
    [projects, statusFilter],
  );

  const statusCounts = useMemo(() => {
    const c = { all: projects.length };
    for (const v of STATUS_VALUES) c[v] = 0;
    for (const p of projects) {
      if (c[p.status] != null) c[p.status] += 1;
    }
    return c;
  }, [projects]);
  const plannerRef = useRef(null);

  // Title text reflecting the rolling 12-month window
  const lastMonth = (anchor.month + 11) % 12;
  const lastYear = anchor.year + Math.floor((anchor.month + 11) / 12);
  const headerRange = `${MONTHS_DE[anchor.month]} ${anchor.year} — ${MONTHS_DE[lastMonth]} ${lastYear}`;

  const shiftAnchor = (deltaMonths) => {
    setAnchor((a) => {
      const total = a.year * 12 + a.month + deltaMonths;
      return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
    });
  };

  return (
    <div className="flex h-screen w-screen bg-slate-50 overflow-hidden" data-testid="planner-page">
      {!sidebarCollapsed && (
        <Sidebar
          projects={visibleProjects}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          expanded={sidebarExpanded}
          onExpandedChange={setSidebarExpanded}
          statusCounts={statusCounts}
          year={anchor.year}
          onPrevYear={() => shiftAnchor(-12)}
          onNextYear={() => shiftAnchor(12)}
          onCreate={handleCreateFromSidebar}
          onSelectProject={handleSelectProject}
          onLogout={logout}
          onCollapse={() => setSidebarCollapsed(true)}
          ownerEmail={user?.email || ""}
        />
      )}

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="h-9 w-9 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-700 shrink-0"
                aria-label="Sidebar einblenden"
                data-testid="sidebar-expand-button"
              >
                <PanelLeftOpen className="w-4 h-4" />
              </button>
            )}
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-400">Jahresübersicht (rollierend)</div>
              <h1
                className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 leading-tight truncate"
                style={{ fontFamily: "Manrope, ui-sans-serif" }}
                data-testid="planner-year-title"
              >
                {headerRange}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* View toggle */}
            <div className="flex items-center rounded-sm border border-slate-200 overflow-hidden mr-1">
              {[
                { value: "dashboard", label: "Dashboard", Icon: LayoutGrid },
                { value: "planner",   label: "Planer",    Icon: CalendarIcon },
                { value: "finance",   label: "Finanzen",  Icon: Euro },
                { value: "list",      label: "Liste",     Icon: ListIcon },
              ].map((tab, idx) => {
                const active = view === tab.value;
                const Icon = tab.Icon;
                return (
                  <button
                    key={tab.value}
                    onClick={() => switchView(tab.value)}
                    className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs tracking-wider uppercase transition-colors ${
                      idx > 0 ? "border-l border-slate-200" : ""
                    } ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`view-${tab.value}-button`}
                  >
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
            <select
              value={plannerSort}
              onChange={(e) => setPlannerSort(e.target.value)}
              disabled={view !== "planner"}
              className="h-9 px-2 rounded-sm border border-slate-200 hover:bg-slate-50 text-xs text-slate-700 tracking-wider uppercase disabled:opacity-40 disabled:cursor-not-allowed bg-white focus:outline-none focus:ring-1 focus:ring-slate-300"
              title="Sortierung der Slot-Spalten"
              data-testid="planner-sort-select"
            >
              <option value="default">Standard</option>
              <option value="milestone">Nach Meilenstein</option>
              <option value="milestone_main">Nach Hauptmeilenstein</option>
            </select>
            {/* Orientation switch */}
            <div className="flex items-center rounded-sm border border-slate-200 overflow-hidden">
              {[
                { value: "vertical", label: "Vertikal" },
                { value: "horizontal", label: "Horizontal" },
              ].map((o, i) => {
                const active = orientation === o.value;
                return (
                  <button
                    key={o.value}
                    onClick={() => setOrientation(o.value)}
                    className={`h-9 px-2.5 text-[11px] tracking-wider uppercase whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
                      i > 0 ? "border-l border-slate-200" : ""
                    } ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`orientation-${o.value}-button`}
                    disabled={view !== "planner"}
                    title={o.value === "horizontal" ? "Horizontale Zeitachse" : "Vertikale Monatsansicht"}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            {/* Horizontal-only zoom dropdown */}
            {view === "planner" && orientation === "horizontal" && (
              <select
                value={String(horizontalZoom)}
                onChange={(e) => setHorizontalZoom(Number(e.target.value))}
                className="h-9 px-2 rounded-sm border border-slate-200 hover:bg-slate-50 text-xs text-slate-700 tabular-nums tracking-wider bg-white focus:outline-none focus:ring-1 focus:ring-slate-300"
                title="Horizontaler Zoom"
                data-testid="horizontal-zoom-select"
              >
                <option value="1">100 %</option>
                <option value="1.5">150 %</option>
                <option value="2">200 %</option>
              </select>
            )}
            <button
              onClick={() => shiftAnchor(-1)}
              className="h-9 w-9 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Vorheriger Monat"
              data-testid="prev-month-button"
              disabled={view !== "planner"}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                const now = new Date();
                setAnchor({ year: now.getFullYear(), month: now.getMonth() });
                // In horizontal mode, force scroll-to-today (anchor may already
                // be current month, in which case no effect would fire).
                if (orientation === "horizontal") {
                  plannerRef.current?.scrollToToday?.();
                }
              }}
              className="h-9 px-3 rounded-sm border border-slate-200 hover:bg-slate-50 text-xs text-slate-600 tracking-wider uppercase disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="scroll-to-today-button"
              disabled={view !== "planner"}
            >
              Heute
            </button>
            <button
              onClick={() => shiftAnchor(1)}
              className="h-9 w-9 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Nächster Monat"
              data-testid="next-month-button"
              disabled={view !== "planner"}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="text-xs text-slate-500 max-w-[300px] text-right leading-relaxed hidden xl:block">
            Klicke eine leere Zelle, um ein Projekt zu erstellen. Konflikte werden rot schraffiert.
          </div>
        </header>
        {view === "dashboard" && (
          <DashboardView
            projects={visibleProjects}
            allProjects={projects}
            onSelectProject={(p) => p?.id && openProject(p)}
            onNavigateToFinance={() => switchView("finance")}
          />
        )}
        {view === "planner" && orientation === "vertical" && (
          <WallPlanner
            ref={plannerRef}
            startYear={anchor.year}
            startMonth={anchor.month}
            projects={visibleProjects}
            specialDays={specialDays}
            onCellClick={handleCellClick}
            onMilestoneDrop={handleMilestoneDrop}
            onPlanningNoteDrop={handlePlanningNoteDrop}
            onProjectSelect={(p) => p?.id && openProject(p)}
            sortMode={plannerSort}
          />
        )}
        {view === "planner" && orientation === "horizontal" && (
          <HorizontalPlanner
            ref={plannerRef}
            startYear={anchor.year}
            startMonth={anchor.month}
            projects={visibleProjects}
            specialDays={specialDays}
            onCellClick={handleCellClick}
            onMilestoneDrop={handleMilestoneDrop}
            onPlanningNoteDrop={handlePlanningNoteDrop}
            onProjectSelect={(p) => p?.id && openProject(p)}
            sortMode={plannerSort}
            zoom={horizontalZoom}
          />
        )}
        {view === "list" && (
          <ProjectListView
            projects={visibleProjects}
            onSelectProject={handleSelectProject}
            restoreState={listStateRef.current}
            onStateChange={(s) => { listStateRef.current = s; }}
          />
        )}
        {view === "finance" && (
          <FinanceView projects={visibleProjects} onSelectProject={handleSelectProject} />
        )}
      </main>

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      <AlertDialog
        open={pendingMilestoneMove !== null}
        onOpenChange={(v) => { if (!v) setPendingMilestoneMove(null); }}
      >
        <AlertDialogContent data-testid="milestone-move-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Meilenstein verschieben?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingMilestoneMove && (
                <>
                  Es liegen noch{" "}
                  <span className="font-semibold text-slate-900">
                    {pendingMilestoneMove.notesBefore.length}
                  </span>{" "}
                  datierte interne Planungs-Notiz
                  {pendingMilestoneMove.notesBefore.length === 1 ? "" : "en"}{" "}
                  vor dem neuen Meilenstein-Datum (
                  {pendingMilestoneMove.newDateIso.split("-").reverse().join(".")}).
                  Trotzdem verschieben?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setPendingMilestoneMove(null)}
              data-testid="milestone-move-cancel"
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const m = pendingMilestoneMove;
                setPendingMilestoneMove(null);
                if (!m) return;
                if (m.savePayload) {
                  // Continue an in-progress dialog save that was blocked by
                  // the planning-notes warning. Bypass the milestone-check.
                  try {
                    const form = m.savePayload;
                    const { id, created_at, ...payload } = form;
                    await axios.put(`${API}/projects/${id}`, payload);
                    toast.success("Projekt aktualisiert");
                    setDialogOpen(false);
                    reload();
                  } catch (e) {
                    toast.error(formatErr(e.response?.data?.detail) || e.message);
                  }
                } else {
                  await commitMilestoneMove(m.project, m.newDateIso);
                }
              }}
              data-testid="milestone-move-confirm-button"
            >
              Trotzdem verschieben
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingNoteMove !== null}
        onOpenChange={(v) => { if (!v) setPendingNoteMove(null); }}
      >
        <AlertDialogContent data-testid="planning-note-move-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Symbol über Hauptmeilenstein verschieben?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingNoteMove && (
                <>
                  Die Planungs-Notiz{" "}
                  <span className="font-semibold text-slate-900">{pendingNoteMove.noteTitle || "(ohne Titel)"}</span>{" "}
                  wechselt die Seite des Hauptmeilensteins (
                  {pendingNoteMove.milestoneDate.split("-").reverse().join(".")}). Trotzdem
                  auf {pendingNoteMove.newDateIso.split("-").reverse().join(".")} verschieben?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setPendingNoteMove(null)}
              data-testid="planning-note-move-cancel"
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const m = pendingNoteMove;
                setPendingNoteMove(null);
                if (!m) return;
                await commitPlanningNoteMove(m.project, m.noteIdx, m.newDateIso);
              }}
              data-testid="planning-note-move-confirm-button"
            >
              Trotzdem verschieben
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
